/**
 * AdminSubmissionsPage — `/submit/history`
 *
 * Shows everything that's been pushed through the Submit page (manual ingest,
 * submit-venue, submit-series) across ALL users. Backed by
 * `GET /api/mgmt/submissions`. Use this to answer "I submitted X — what
 * happened to it?" without grepping logs.
 *
 * Regular users get a self-scoped version of this at `/submit/mine`
 * (`MySubmissionsPage`); both render the shared `SubmissionsTable`.
 */

import { useEffect, useMemo, useState } from 'react';
import SectionTabs from '../../components/SectionTabs';
import { SUBMIT_TABS } from '../../lib/section-tabs';
import { api } from '../../lib/api';
import { LoadingBlock } from '../../components/Spinner';
import SubmissionsTable, {
  type SubmissionKind,
  type SubmissionStatus,
  type SubmissionsResponse
} from '../../components/SubmissionsTable';

export default function AdminSubmissionsPage() {
  const [data, setData] = useState<SubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [kind, setKind] = useState<'all' | SubmissionKind>('all');
  const [status, setStatus] = useState<'all' | SubmissionStatus>('all');
  const [monthsBack, setMonthsBack] = useState(3);

  const reload = useMemo(
    () => () => {
      setLoading(true);
      setErr(null);
      const params = new URLSearchParams();
      params.set('limit', '200');
      params.set('monthsBack', String(monthsBack));
      if (kind !== 'all') params.set('kind', kind);
      if (status !== 'all') params.set('status', status);
      api
        .get<SubmissionsResponse>(`/api/mgmt/submissions?${params.toString()}`)
        .then(setData)
        .catch((e) => setErr(String(e)))
        .finally(() => setLoading(false));
    },
    [kind, status, monthsBack]
  );

  useEffect(() => { reload(); }, [reload]);

  const items = data?.items ?? [];
  const counts = useMemo(() => {
    const c = { total: items.length, success: 0, error: 0, venue: 0, series: 0, ingest: 0, compose: 0 };
    for (const i of items) {
      if (i.status === 'success') c.success++; else c.error++;
      if (i.kind in c) (c as Record<string, number>)[i.kind]++;
    }
    return c;
  }, [items]);

  return (
    <SectionTabs ariaLabel="Submit sections" items={SUBMIT_TABS}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">Submissions</h1>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={reload}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <p className="text-muted small">
        Audit trail of every Submit-page request (event/file/text ingest, venue
        submissions, series submissions) from all users. Use this to confirm that
        a venue or series that was submitted was actually created — and if it
        wasn't, to read the error message that came back.
      </p>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label className="form-label small mb-1" htmlFor="sub-kind">Kind</label>
          <select
            id="sub-kind"
            className="form-select form-select-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'all' | SubmissionKind)}
          >
            <option value="all">All ({counts.total})</option>
            <option value="venue">Venue ({counts.venue})</option>
            <option value="series">Series ({counts.series})</option>
            <option value="ingest">Event / file ({counts.ingest})</option>
            <option value="compose">Composed event ({counts.compose})</option>
          </select>
        </div>
        <div>
          <label className="form-label small mb-1" htmlFor="sub-status">Status</label>
          <select
            id="sub-status"
            className="form-select form-select-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'all' | SubmissionStatus)}
          >
            <option value="all">All</option>
            <option value="success">Success ({counts.success})</option>
            <option value="error">Error ({counts.error})</option>
          </select>
        </div>
        <div>
          <label className="form-label small mb-1" htmlFor="sub-months">Months back</label>
          <select
            id="sub-months"
            className="form-select form-select-sm"
            value={monthsBack}
            onChange={(e) => setMonthsBack(Number(e.target.value))}
          >
            {[1, 2, 3, 6, 12].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {err && <div className="alert alert-danger" role="alert">{err}</div>}
      {loading && !data && <LoadingBlock label="Loading submissions…" />}

      {data && items.length === 0 && (
        <div className="alert alert-info" role="status">
          No submissions match the current filters.
        </div>
      )}

      {items.length > 0 && <SubmissionsTable items={items} showUser />}
    </SectionTabs>
  );
}
